/**
 * Cache rules for the production service worker.
 *
 * Hashed build assets stay on Workbox's precache (cache-first). HTML is
 * precached only so the app can still boot offline; navigations must not use
 * that copy while the network is up, or a hard reload can pair a new
 * index.html (new JS) with a stylesheet still stored under the previous build.
 */

export const HTML_CACHE = "palmtree-html";

/** Session flag: the user already asked to reload for this update. */
export const UPDATE_RELOAD_FLAG = "pwa-update-reload";

export function isNavigationRequest(request: {
  mode: string;
  method?: string;
  url?: string;
}): boolean {
  if (request.method && request.method !== "GET") return false;
  if (request.mode !== "navigate") return false;
  if (!request.url) return true;
  try {
    if (new URL(request.url).pathname.includes("/assets/")) return false;
  } catch {
    return true;
  }
  return true;
}

/**
 * Caches this worker owns. Anything else (another origin's caches, future
 * app caches) is left alone. The current precache and the HTML runtime cache
 * are kept; previous build caches are not.
 */
export function cachesToDelete(
  existing: readonly string[],
  keep: readonly string[],
): string[] {
  const keepSet = new Set(keep);
  return existing.filter((name) => {
    if (keepSet.has(name)) return false;
    return name.startsWith("workbox-precache-") || name.startsWith("palmtree-");
  });
}

export async function cleanupCachesOnActivate(
  keys: () => Promise<string[]>,
  remove: (name: string) => Promise<unknown>,
  keep: readonly string[],
): Promise<string[]> {
  const stale = cachesToDelete(await keys(), keep);
  await Promise.all(stale.map((name) => remove(name)));
  return stale;
}

/** One shell entry per document URL, ignoring the update-test query string. */
export function htmlCacheRequest(request: Request): Request {
  const url = new URL(request.url);
  url.search = "";
  url.hash = "";
  return new Request(url.href, { method: "GET" });
}

/**
 * Fetch HTML with a reload cache mode so the HTTP cache cannot return a
 * stale index.html while we are online. Navigation requests cannot be
 * passed to fetch() as-is (their mode is `navigate`).
 */
export function networkHtmlRequest(request: Request): Request {
  return new Request(request.url, {
    method: "GET",
    credentials: "same-origin",
    redirect: "follow",
    cache: "reload",
  });
}

type HtmlCache = {
  match: (request: Request) => Promise<Response | undefined>;
  put: (request: Request, response: Response) => Promise<unknown>;
};

function isNoStore(response: Response): boolean {
  const value = response.headers.get("Cache-Control") ?? "";
  return /(?:^|,)\s*no-store\b/i.test(value);
}

/**
 * Online: the network document wins, even if an older shell is cached.
 * Offline: the last good document, then the precached shell.
 */
export async function networkFirstNavigation(
  request: Request,
  deps: {
    fetch: (request: Request) => Promise<Response>;
    openCache: (name: string) => Promise<HtmlCache>;
    matchPrecache: () => Promise<Response | undefined>;
  },
): Promise<Response> {
  try {
    const response = await deps.fetch(networkHtmlRequest(request));
    if (response.ok && response.type !== "opaque" && !isNoStore(response)) {
      try {
        const cache = await deps.openCache(HTML_CACHE);
        await cache.put(htmlCacheRequest(request), response.clone());
      } catch {
        // A failed cache write must not hide the fresh document.
      }
    }
    return response;
  } catch {
    try {
      const cache = await deps.openCache(HTML_CACHE);
      const cached = await cache.match(htmlCacheRequest(request));
      if (cached) return cached;
    } catch {
      // Fall through to the precached shell.
    }
    const shell = await deps.matchPrecache();
    if (shell) return shell;
    return new Response("Offline", {
      status: 503,
      headers: { "Content-Type": "text/html; charset=utf-8" },
    });
  }
}

export function takeUpdateReloadSuppression(storage: {
  getItem: (key: string) => string | null;
  removeItem: (key: string) => void;
}): boolean {
  if (storage.getItem(UPDATE_RELOAD_FLAG) !== "1") return false;
  storage.removeItem(UPDATE_RELOAD_FLAG);
  return true;
}
